/* eslint-disable no-param-reassign, no-underscore-dangle */
import L from 'leaflet';
import Geocoding from 'esri-leaflet-geocoder';
import { isEmpty, find, forEach, isNil } from 'lodash';

import { styleFetchOne } from '../../api/Styles/style-methods-client';
import * as layerOps from '../../api/Layers/layer-operations';
import * as mapOps from '../../api/Maps/map-operations';
import * as styleOps from '../../api/Styles/style-operations';

import getDefaultStyle from '../get-default-style';

import customCollapseButton from './customCollapseButton';
import customFileInputButton from './customFileInputButton';
import customGotoLocationButton from './customGotoLocationButton';
import customHomeButton from './customHomeButton';
import './customLayerControl';
import customLocationButton from './customLocationButton';
import customMapsAttribution from './customMapsAttribution';
import customQRButton from './customQRButton';
import customReloadButton from './customReloadButton';
import customScale from './customScale';
import customTitleButton from './customTitleButton';
import customZoom from './customZoomControl';

const drawCustomButtons = ({
  baseMaps = [],
  baseMapsToDisplay = [],
  baseMapsZoom = {},
  currentLocationState = false,
  fileInputButton = false,
  goToState = false,
  gotoLocationButton = false,
  gotoLocationFunction = () => '',
  layers = [],
  layersToDisplay = [],
  layersZoom = {},
  leafletMap,
  loadFileFunction = () => '',
  locationButton = false,
  locationFunction = () => '',
  map,
  reloadButton = false,
  reloadFunction = () => '',
  type
}) => {
  if (leafletMap.titleButton) leafletMap.removeControl(leafletMap.titleButton);
  if (leafletMap.homeButton) leafletMap.removeControl(leafletMap.homeButton);
  if (leafletMap.customScale) leafletMap.removeControl(leafletMap.customScale);
  if (leafletMap.layerControl) leafletMap.removeControl(leafletMap.layerControl);
  if (leafletMap.baseMapControl) leafletMap.removeControl(leafletMap.baseMapControl);
  if (leafletMap.searchControl) leafletMap.removeControl(leafletMap.searchControl);
  if (leafletMap.attributionControl) leafletMap.removeControl(leafletMap.attributionControl);
  if (leafletMap.qrControl) leafletMap.removeControl(leafletMap.qrControl);
  if (leafletMap.customZoomButton) leafletMap.removeControl(leafletMap.customZoomButton);
  if (leafletMap.zoomControl) leafletMap.removeControl(leafletMap.zoomControl);
  if (leafletMap.collapseButton) leafletMap.removeControl(leafletMap.collapseButton);
  if (leafletMap.fileInputButton) leafletMap.removeControl(leafletMap.fileInputButton);
  if (leafletMap.reloadButton) leafletMap.removeControl(leafletMap.reloadButton);
  if (leafletMap.locationButton) leafletMap.removeControl(leafletMap.locationButton);
  if (leafletMap.gotoLocationButton) leafletMap.removeControl(leafletMap.gotoLocationButton);

  const showBaseMapSelector = mapOps.showBaseMapSelector(map) && !isEmpty(baseMaps);
  const showLayerSelector = mapOps.showLayerSelector(map) && !isEmpty(layers);
  const layerControls = L.control.customlayercontrol(null, null, {
    autoZIndex: false
  });
  const baseMapsControls = L.control.customlayercontrol(null, null, {
    autoZIndex: false
  });
  const searchControl = Geocoding.geosearch({ useMapBounds: false });

  if (mapOps.showCollapseButton(map)) customCollapseButton(leafletMap);

  if (mapOps.showZoomButton(map)) customZoom(leafletMap);

  if (mapOps.showSearchButton(map)) {
    leafletMap.addControl(searchControl);
    leafletMap.searchControl = searchControl;
  }

  if (!mapOps.showAttributionButton(map))
    customMapsAttribution(leafletMap, mapOps.showAttributionButton(map));
  else
    customMapsAttribution(leafletMap, mapOps.showAttributionButton(map), [
      mapOps.getPageURL(map),
      mapOps.getLogoURL(map)
    ]);

  if (mapOps.showScale(map)) customScale(leafletMap);

  forEach(baseMapsToDisplay, async (baseMap, name) => {
    const mapBaseMap = find(baseMaps, ['name', name]);
    const layerStyle =
      (await styleFetchOne(layerOps.getStyleId(mapBaseMap))) || getDefaultStyle(mapBaseMap);
    if (
      !isNil(baseMap) &&
      (styleOps.isGeoJSON(layerStyle) || styleOps.isMapBox(layerStyle)) &&
      mapOps.showLegend(map)
    )
      baseMap.layerStyle = layerStyle;

    const baseMapZoom = find(baseMapsZoom, ['id', baseMap.ipsilumId]);
    baseMap.maxZoom = isNil(baseMapZoom) ? layerOps.getMaxZoom(baseMap) : baseMapZoom.maxZoom;
    baseMap.minZoom = isNil(baseMapZoom) ? layerOps.getMinZoom(baseMap) : baseMapZoom.minZoom;

    try {
      baseMapsControls.addBaseLayer(baseMap, name);
    } catch (error) {
      console.error(error);
    }
  });

  forEach(layersToDisplay, async (layer, name) => {
    const mapLayer = find(layers, ['name', name]);
    const layerStyle =
      (await styleFetchOne(layerOps.getStyleId(mapLayer))) || getDefaultStyle(mapLayer);
    if (
      !isNil(layer) &&
      (styleOps.isGeoJSON(layerStyle) || styleOps.isMapBox(layerStyle)) &&
      mapOps.showLegend(map)
    )
      layer.layerStyle = layerStyle;

    const layerZoom = find(layersZoom, ['id', layer.ipsilumId]);
    layer.maxZoom = isNil(layerZoom) ? layerOps.getMaxZoom(layer) : layerZoom.maxZoom;
    layer.minZoom = isNil(layerZoom) ? layerOps.getMinZoom(layer) : layerZoom.minZoom;

    try {
      if (mapOps.areLayersOverlayed(map)) layerControls.addOverlay(layer, name);
      else layerControls.addBaseLayer(layer, name);
    } catch (error) {
      console.error(error);
    }
  });

  if (showBaseMapSelector) {
    leafletMap.addControl(baseMapsControls);
    baseMapsControls.changeStyleOnlyBaseMaps();
  }

  if (showLayerSelector) {
    leafletMap.addControl(layerControls);
    layerControls.changeStyleOnlyLayers();
  }

  if (showBaseMapSelector && showLayerSelector) {
    layerControls.changeStyleLayers();
    baseMapsControls.changeStyleBaseMaps();
  }

  if (fileInputButton) customFileInputButton(leafletMap, loadFileFunction);

  if (reloadButton) customReloadButton(leafletMap, reloadFunction);

  if (locationButton) customLocationButton(leafletMap, locationFunction, currentLocationState);

  if (gotoLocationButton) customGotoLocationButton(leafletMap, gotoLocationFunction, goToState);

  if (mapOps.showHomeButton(map)) customHomeButton(leafletMap, mapOps.getMapLocation(map));

  if (mapOps.showTitleButton(map)) customTitleButton(leafletMap, mapOps.getName(map));

  if (mapOps.showQRCode(map) && mapOps.isShared(map))
    customQRButton({
      leafletMap,
      type,
      publicId: mapOps.getPublicId(map),
      sharedPrivate: map.sharedPrivate
    });
};

export default drawCustomButtons;
